import Icon from '../Icon';
import LoadingIndicator from '../LoadingIndicator';
import { DEFAULT_TOOL_TIP_DELAY } from '../Tooltip/Tooltip.constants';
import { browserIsFirefox } from '../utils/browserId';
import type React from 'react';
import type { ComponentProps } from 'react';
import type { QTipDataAttributes } from '../Tooltip/QTip.types';
import type { ButtonProps } from './Button.types';

type ButtonComponentProps = ButtonProps &
  React.HtmlHTMLAttributes<HTMLButtonElement> &
  React.RefAttributes<HTMLButtonElement>;
type LoadingIndicatorSize = ComponentProps<typeof LoadingIndicator>['size'];

/**
 * All-in-one Button:
 *  - use "variant" to achieve the desired style
 *  - include tooltips and/or icons
 */
const Button = ({
  onClick,
  label,
  variant = 'outline',
  type = 'button',
  size = 'sm',
  disabled,
  inProgress = false,
  extraClassNames,
  id,
  testId,
  stopPropagation = true,
  tooltip,
  tooltipOptions,
  iconStyle = 'text',
  icon,
  iconColor,
  iconPosition = 'left',
  iconPrefix,
  preventBlur = false,
  isHtmlTooltip = false,
  tooltipTestId,
  'aria-label': ariaLabel,
  ref,
  ...rest
}: ButtonComponentProps) => {
  const baseClasses =
    'tw:px-2.5 tw:rounded-md tw:focus:ring-0 tw:focus-visible:outline-none tw:hover:cursor-pointer tw:aria-disabled:cursor-not-allowed tw:disabled:pointer-events-none';
  const baseClassesByVariant = {
    'outline': 'tw:disabled:opacity-50 tw:aria-disabled:opacity-50 tw:border-solid tw:border',
    'theme': 'tw:disabled:opacity-50 tw:aria-disabled:opacity-50 tw:border-solid tw:border',
    'danger':
      'tw:bg-sq-danger tw:disabled:opacity-50 tw:aria-disabled:opacity-50 ' +
      'tw:border-solid tw:border tw:border-sq-danger',
    'theme-light': 'tw:disabled:opacity-50 tw:aria-disabled:opacity-50 tw:border-solid tw:border',
    'no-border': 'tw:disabled:opacity-50 tw:aria-disabled:opacity-50',
    'navigation': 'tw:disabled:opacity-50 tw:aria-disabled:opacity-50',
    'navigation-selected': 'tw:disabled:opacity-50 tw:aria-disabled:opacity-50',
    'warning':
      'tw:bg-sq-warning tw:border-solid tw:border tw:border-sq-warning ' +
      'tw:disabled:opacity-50 tw:aria-disabled:opacity-50',
  };
  const textClassesByVariantLightTheme = {
    'outline': 'tw:text-sq-text-color',
    'theme': 'tw:text-sq-white',
    'theme-light': 'tw:text-sq-white',
    'danger': 'tw:text-sq-white',
    'no-border': 'tw:text-sq-text-color',
    'navigation': 'tw:text-sq-text-color',
    'navigation-selected': 'tw:text-sq-theme-link',
    'warning': 'tw:text-sq-white',
  };
  const textClassesByVariantDarkTheme = {
    'outline': 'tw:dark:text-sq-dark-text',
    'theme': 'tw:dark:text-sq-white',
    'theme-light': 'tw:dark:text-sq-white',
    'danger': 'tw:dark:text-sq-white',
    'no-border': 'tw:dark:text-sq-dark-text',
    'navigation': 'tw:dark:text-sq-dark-text',
    'navigation-selected': 'tw:dark:text-sq-dark-text',
    'warning': 'tw:dark:text-sq-white',
  };

  const classesByVariantLightTheme = {
    'outline': 'tw:bg-white tw:border-sq-disabled-gray',
    'theme': 'tw:bg-sq-theme-dark tw:border-sq-theme-dark',
    'danger': '',
    'theme-light': 'tw:bg-sq-theme-icon tw:border-sq-theme-icon',
    'no-border': '',
    'navigation': 'tw:bg-transparent',
    'navigation-selected': 'tw:bg-sq-surface!',
    'warning': '',
  };

  const classesByVariantDarkTheme = {
    'outline': 'tw:dark:bg-sq-dark-background tw:dark:border-sq-dark-disabled-gray',
    'theme': 'tw:dark:bg-sq-theme-dark tw:dark:border-sq-theme-dark',
    'danger': '',
    'theme-light': 'tw:dark:bg-sq-theme-icon tw:dark:border-sq-theme-icon',
    'no-border': '',
    'navigation': 'tw:dark:bg-transparent',
    'navigation-selected': 'tw:dark:bg-sq-dark-background!',
    'warning': '',
  };

  const activeClassesByVariantLightTheme = {
    'outline':
      'tw:hover:bg-sq-light-gray tw:focus:bg-sq-dark-gray tw:active:bg-sq-dark-gray tw:focus:border-sq-theme-dark' +
      ' tw:active:border-sq-theme-dark',
    'theme':
      'tw:hover:bg-sq-theme-highlight tw:hover:border-sq-theme-highlight tw:focus:bg-sq-theme-darker ' +
      'tw:focus:border-sq-theme-darker tw:active:bg-sq-theme-darker tw:active:border-sq-theme-darker',
    'danger':
      'tw:hover:bg-sq-danger-hover tw:hover:border-sq-danger-hover tw:focus:bg-sq-danger-hover ' +
      'tw:focus:border-sq-danger-hover tw:active:bg-sq-danger tw:active:border-sq-danger',
    'theme-light':
      'tw:hover:bg-sq-theme-link tw:hover:border-sq-theme-link tw:focus:bg-sq-theme-darker tw:focus:border-sq-theme-darker ' +
      'tw:active:bg-sq-theme-darker tw:active:border-sq-theme-darker',
    'no-border': 'tw:hover:bg-sq-light-gray tw:focus:bg-sq-light-gray tw:active:bg-sq-light-gray',
    'navigation': 'tw:hover:bg-sq-surface! tw:focus:bg-sq-surface! tw:active:bg-sq-surface!',
    'navigation-selected': 'tw:hover:bg-sq-surface! tw:focus:bg-sq-surface! tw:active:bg-sq-surface!',
    'warning':
      'tw:hover:bg-sq-warning-hover tw:hover:border-sq-warning-hover tw:focus:bg-sq-warning-hover ' +
      'tw:focus:border-sq-warning-hover tw:active:bg-sq-warning tw:active:border-sq-warning',
  };

  const activeClassesByVariantDarkTheme = {
    'outline':
      'tw:dark:hover:bg-sq-highlight-color-dark tw:dark:focus:bg-sq-multi-gray-dark' +
      ' tw:dark:active:bg-sq-multi-gray-dark tw:dark:focus:border-sq-theme-dark tw:dark:active:border-sq-theme-dark',
    'theme':
      'tw:dark:hover:bg-sq-theme-highlight tw:dark:hover:border-sq-theme-highlight tw:dark:focus:bg-sq-theme-darker ' +
      'tw:dark:focus:border-sq-theme-darker tw:dark:active:bg-sq-theme-darker tw:dark:active:border-sq-theme-darker',
    'danger':
      'tw:dark:hover:bg-sq-danger-hover tw:dark:hover:border-sq-danger-hover tw:dark:focus:bg-sq-danger-hover ' +
      'tw:dark:focus:border-sq-danger-hover tw:dark:active:bg-sq-danger tw:dark:active:border-sq-danger',
    'theme-light':
      'tw:dark:hover:bg-sq-theme-link tw:dark:hover:border-sq-theme-link tw:dark:focus:bg-sq-theme-darker ' +
      'tw:dark:focus:border-sq-theme-darker tw:dark:active:bg-sq-theme-darker tw:dark:active:border-sq-theme-darker',
    'no-border':
      'tw:dark:hover:bg-sq-highlight-color-dark tw:dark:focus:bg-sq-highlight-color-dark tw:dark:active:bg-sq-highlight-color-dark',
    'navigation':
      'tw:dark:hover:bg-sq-dark-background! tw:dark:focus:bg-sq-dark-background! tw:dark:active:bg-sq-dark-background!',
    'navigation-selected':
      'tw:dark:hover:bg-sq-dark-background! tw:dark:focus:bg-sq-dark-background! tw:dark:active:bg-sq-dark-background!',
    'warning':
      'tw:dark:hover:bg-sq-warning-hover tw:dark:hover:border-sq-warning-hover tw:dark:focus:bg-sq-warning-hover ' +
      'tw:dark:focus:border-sq-warning-hover tw:dark:active:bg-sq-warning tw:dark:active:border-sq-warning',
  };
  const sizeClasses = {
    xs: 'tw:text-xs tw:py-0.5',
    sm: 'tw:text-sm tw:py-1',
    lg: 'tw:text-xl tw:py-1',
  };
  const loadingIndicatorSizes: Record<typeof size, LoadingIndicatorSize> = {
    xs: 'xs',
    sm: 'sm',
    lg: 'md',
  };
  let tooltipData: QTipDataAttributes | undefined;
  let appliedClasses = `${baseClasses} ${baseClassesByVariant[variant]} ${sizeClasses[size]} ${classesByVariantLightTheme[variant]} ${classesByVariantDarkTheme[variant]} ${textClassesByVariantLightTheme[variant]} ${textClassesByVariantDarkTheme[variant]} ${extraClassNames}`;
  if (!disabled) {
    appliedClasses += ` ${activeClassesByVariantLightTheme[variant]} ${activeClassesByVariantDarkTheme[variant]}`;
  }

  if (tooltip) {
    tooltipData = {
      'data-qtip-text': tooltip,
      'data-qtip-placement': tooltipOptions?.position,
      'data-qtip-is-html': isHtmlTooltip,
      'data-qtip-testid': tooltipTestId,
      'data-qtip-delay': tooltipOptions?.delay ?? DEFAULT_TOOL_TIP_DELAY,
    };
  }

  const resolvedAriaLabel = ariaLabel ?? (!label && icon ? tooltip : undefined);

  const iconClass = iconPosition === 'left' ? 'tw:mr-1' : 'tw:ml-1';
  const iconExtraClassNames = label
    ? `${iconClass} ${textClassesByVariantLightTheme[variant]} ${textClassesByVariantDarkTheme[variant]}`
    : '';
  const iconTestId = id ? `${id}_spinner` : undefined;
  const loadingIndicatorTestId = iconTestId ?? (testId ? `${testId}_spinner` : undefined);
  const iconElement = inProgress ? (
    <LoadingIndicator
      size={loadingIndicatorSizes[size]}
      color={iconStyle === 'color' && iconColor ? iconColor : 'currentColor'}
      extraClassNames={iconExtraClassNames}
      testId={loadingIndicatorTestId}
    />
  ) : (
    icon && (
      <Icon
        icon={icon}
        iconPrefix={iconPrefix}
        type={iconStyle}
        color={iconColor}
        extraClassNames={iconExtraClassNames}
        testId={iconTestId}
      />
    )
  );

  return (
    <button
      {...rest}
      ref={ref}
      id={id}
      {...tooltipData}
      disabled={disabled && !tooltip}
      aria-disabled={disabled}
      aria-label={resolvedAriaLabel}
      data-testid={testId}
      tabIndex={disabled ? -1 : undefined}
      type={type === 'link' || (type === 'submit' && browserIsFirefox) ? 'button' : type}
      onClick={(e: React.MouseEvent) => {
        stopPropagation && e.stopPropagation();
        if (!disabled) {
          onClick?.(e);
        }
      }}
      onMouseDown={(e) => {
        if (preventBlur) {
          e.preventDefault();
        }
      }}
      className={appliedClasses}>
      {iconPosition === 'left' && iconElement}
      {label}
      {iconPosition === 'right' && iconElement}
    </button>
  );
};
export default Button;
